import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject, forkJoin } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { AppConstants } from '../../../../app.constants';
import { LoggerService } from '../../../../core/logger/logger.service';
import { OrgSelectorService } from '../../../../core/services/org-selector.service';
import {
	BulkAssignWhitelabelDto,
	CreateWhitelabelDto,
	OrganizationWhitelabelAssignmentDto,
	UpdateWhitelabelDto,
} from '../../../../modules/organizations/dto/whitelabel.dto';
import { OrganizationsService } from '../../../../modules/organizations/organizations.service';
import {
	formatWhitelabelLabel,
	Whitelabel,
} from '../../../../shared/models/whitelabel.model';
import { RouteUtils } from '../../../../shared/utils/route/route.util';

const trackWhitelabelById = (index: number, whitelabel: Whitelabel): string => {
	void index;

	return whitelabel.id;
};

const trackOrganizationById = (
	index: number,
	organization: OrganizationWhitelabelAssignmentDto,
): string => {
	void index;

	return organization.id;
};

@Component({
	selector: 'app-whitelabel',
	styleUrls: ['./whitelabel.component.scss'],
	templateUrl: './whitelabel.component.html',
})
export class WhitelabelComponent implements OnInit, OnDestroy {
	public assignmentForm!: FormGroup;
	public editingWhitelabelId: string | null = null;
	public isLoading = true;
	public isSavingAssignment = false;
	public isSavingWhitelabel = false;
	public isUnauthorizedSanusAccess = false;
	public matrices: OrganizationWhitelabelAssignmentDto[] = [];
	public orgId: string = '';
	public organizationName = '';
	public searchTerm = '';
	public selectedOrganizationIds = new Set<string>();
	public readonly trackOrganization = trackOrganizationById;
	public readonly trackWhitelabel = trackWhitelabelById;
	public whitelabelForm!: FormGroup;
	public whitelabels: Whitelabel[] = [];

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: FormBuilder,
		private readonly activatedRoute: ActivatedRoute,
		private readonly loggerService: LoggerService,
		private readonly orgSelectorService: OrgSelectorService,
		private readonly organizationsService: OrganizationsService,
		private readonly router: Router,
	) {}

	get allSelected(): boolean {
		return (
			this.filteredMatrices.length > 0 &&
			this.filteredMatrices.every((organization) =>
				this.selectedOrganizationIds.has(organization.id),
			)
		);
	}

	get filteredMatrices(): OrganizationWhitelabelAssignmentDto[] {
		const normalizedSearch = this.searchTerm.trim().toLocaleLowerCase('pt-BR');

		if (!normalizedSearch) {
			return this.matrices;
		}

		return this.matrices.filter((organization) => {
			const primaryName = organization.name_secondary || organization.name;
			const searchableValue =
				`${primaryName} ${organization.name}`.toLocaleLowerCase('pt-BR');

			return searchableValue.includes(normalizedSearch);
		});
	}

	get hasSelection(): boolean {
		return this.selectedOrganizationIds.size > 0;
	}

	get selectedCount(): number {
		return this.selectedOrganizationIds.size;
	}

	get selectedWhitelabelLabel(): string {
		const selectedId = this.assignmentForm?.get('whitelabelId')?.value;

		if (!selectedId) {
			return 'Nenhum whitelabel selecionado';
		}

		return this.getWhitelabelNameById(selectedId);
	}

	applyWhitelabel(): void {
		if (
			this.assignmentForm.invalid ||
			!this.hasSelection ||
			this.isSavingAssignment
		) {
			return;
		}

		const dto: BulkAssignWhitelabelDto = {
			organizationIds: Array.from(this.selectedOrganizationIds),
			whitelabelId: this.assignmentForm.get('whitelabelId')?.value,
		};

		this.isSavingAssignment = true;
		this.organizationsService
			.assignWhitelabelToOrganizations(this.orgId, dto)
			.pipe(finalize(() => (this.isSavingAssignment = false)))
			.subscribe({
				error: (err) => {
					this.loggerService.error(
						'Erro ao atribuir whitelabel às matrizes',
						err,
					);
				},
				next: () => {
					this.loggerService.info('Whitelabel atribuído com sucesso.');
					this.selectedOrganizationIds.clear();
					this.assignmentForm.patchValue({ whitelabelId: '' });
					this.reloadData();
				},
			});
	}

	cancelEdit(): void {
		this.editingWhitelabelId = null;
		this.whitelabelForm.reset({ name: '' });
	}

	editWhitelabel(whitelabel: Whitelabel): void {
		this.editingWhitelabelId = whitelabel.id;
		this.whitelabelForm.patchValue({
			name: whitelabel.name,
		});
	}

	formatWhitelabel(whitelabel: Whitelabel | null | undefined): string {
		return formatWhitelabelLabel(whitelabel);
	}

	getWhitelabelNameById(id: string): string {
		const whitelabel = this.whitelabels.find((item) => item.id === id);

		return formatWhitelabelLabel(whitelabel) || 'Whitelabel não encontrado';
	}

	isOrganizationSelected(organizationId: string): boolean {
		return this.selectedOrganizationIds.has(organizationId);
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	ngOnInit(): void {
		this.initForms();
		this.loadData();
	}

	onOrganizationKeydown(event: KeyboardEvent, organizationId: string): void {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			this.toggleOrganization(organizationId);
		}
	}

	onSearchChange(value: string): void {
		this.searchTerm = value;
	}

	removeWhitelabel(): void {
		if (!this.hasSelection || this.isSavingAssignment) {
			return;
		}

		const dto: BulkAssignWhitelabelDto = {
			organizationIds: Array.from(this.selectedOrganizationIds),
			whitelabelId: null,
		};

		this.isSavingAssignment = true;
		this.organizationsService
			.assignWhitelabelToOrganizations(this.orgId, dto)
			.pipe(finalize(() => (this.isSavingAssignment = false)))
			.subscribe({
				error: (err) => {
					this.loggerService.error(
						'Erro ao remover whitelabel das matrizes',
						err,
					);
				},
				next: () => {
					this.loggerService.info('Whitelabel removido com sucesso.');
					this.selectedOrganizationIds.clear();
					this.reloadData();
				},
			});
	}

	saveWhitelabel(): void {
		if (this.whitelabelForm.invalid || this.isSavingWhitelabel) {
			return;
		}

		const formValue = this.whitelabelForm.getRawValue();
		const normalizedName = formValue.name?.trim();

		this.isSavingWhitelabel = true;
		const request$ = this.editingWhitelabelId
			? this.organizationsService.updateWhitelabel(
					this.orgId,
					this.editingWhitelabelId,
					{
						name: normalizedName,
					} satisfies UpdateWhitelabelDto,
				)
			: this.organizationsService.createWhitelabel(this.orgId, {
					name: normalizedName,
				} satisfies CreateWhitelabelDto);

		request$.pipe(finalize(() => (this.isSavingWhitelabel = false))).subscribe({
			error: (err) => {
				this.loggerService.error('Erro ao salvar whitelabel', err);
			},
			next: () => {
				this.loggerService.info(
					this.editingWhitelabelId
						? 'Whitelabel atualizado com sucesso.'
						: 'Whitelabel criado com sucesso.',
				);
				this.cancelEdit();
				this.reloadData();
			},
		});
	}

	toggleAllOrganizations(): void {
		if (this.allSelected) {
			this.filteredMatrices.forEach((organization) =>
				this.selectedOrganizationIds.delete(organization.id),
			);
		} else {
			this.filteredMatrices.forEach((organization) =>
				this.selectedOrganizationIds.add(organization.id),
			);
		}
	}

	toggleOrganization(id: string): void {
		if (this.selectedOrganizationIds.has(id)) {
			this.selectedOrganizationIds.delete(id);
		} else {
			this.selectedOrganizationIds.add(id);
		}
	}

	private getRouteOrganizationId(): string {
		let currentRoute: ActivatedRoute | null = this.activatedRoute;

		while (currentRoute) {
			const params = currentRoute.snapshot.paramMap;
			const organizationId =
				params.get(RouteUtils.params.organizationId.value) ?? params.get('id');

			if (organizationId) {
				return organizationId;
			}

			currentRoute = currentRoute.parent;
		}

		throw new Error(
			`O parâmetro ${RouteUtils.params.organizationId.name} está ausente na rota`,
		);
	}

	private initForms(): void {
		this.whitelabelForm = this.fb.group({
			name: ['', [Validators.required]],
		});

		this.assignmentForm = this.fb.group({
			whitelabelId: ['', [Validators.required]],
		});
	}

	private loadData(): void {
		this.activatedRoute.paramMap
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(() => {
				const organizationId = this.getRouteOrganizationId();

				if (organizationId) {
					this.loggerService.debug('Whitelabel route context', {
						organizationIdFromRoute: organizationId,
						selectedOrganizationId: this.orgSelectorService.getOrganizationId(),
						selectedOrganizationName:
							this.orgSelectorService.getOrganizationName(),
						userOrganizationId: this.orgSelectorService.getUserOrganization(),
					});

					this.orgId = organizationId;
					this.loadOrganizationContext(organizationId);
				}
			});
	}

	private loadOrganizationContext(organizationId: string): void {
		this.isLoading = true;
		this.loggerService.debug('Loading whitelabel organization context', {
			organizationId,
			selectedOrganizationId: this.orgSelectorService.getOrganizationId(),
			userOrganizationId: this.orgSelectorService.getUserOrganization(),
		});

		this.organizationsService
			.getById(organizationId)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (err) => {
					this.isUnauthorizedSanusAccess = true;
					this.isLoading = false;
					this.loggerService.error('Erro ao carregar organização', {
						error: err,
						organizationId,
						selectedOrganizationId: this.orgSelectorService.getOrganizationId(),
						userOrganizationId: this.orgSelectorService.getUserOrganization(),
					});
				},
				next: (organization) => {
					this.loggerService.debug('Whitelabel organization payload loaded', {
						id: organization.id,
						is_matriz: organization.is_matriz,
						name_secondary: organization.name_secondary,
					});

					this.organizationName = organization.name_secondary;

					if (organization.id !== AppConstants.organization.id) {
						this.isUnauthorizedSanusAccess = true;
						this.isLoading = false;
						this.loggerService.error(
							'Funcionalidade disponível apenas no contexto da Sanus.',
						);

						return;
					}

					this.isUnauthorizedSanusAccess = false;
					this.reloadData();
				},
			});
	}

	private reloadData(): void {
		this.loggerService.debug('Reloading whitelabel data', {
			organizationId: this.orgId,
			selectedOrganizationId: this.orgSelectorService.getOrganizationId(),
			userOrganizationId: this.orgSelectorService.getUserOrganization(),
		});

		forkJoin([
			this.organizationsService.getWhitelabels(this.orgId),
			this.organizationsService.getWhitelabelMatrices(this.orgId),
		])
			.pipe(
				takeUntil(this.unsubscribe$),
				finalize(() => (this.isLoading = false)),
			)
			.subscribe({
				error: (err) => {
					this.isUnauthorizedSanusAccess = true;
					this.loggerService.error('Erro ao carregar dados de whitelabel', {
						error: err,
						organizationId: this.orgId,
						selectedOrganizationId: this.orgSelectorService.getOrganizationId(),
						userOrganizationId: this.orgSelectorService.getUserOrganization(),
					});
				},
				next: ([whitelabels, matrices]) => {
					this.whitelabels = whitelabels;
					this.matrices = matrices;
					this.removeUnavailableSelections();
				},
			});
	}

	private removeUnavailableSelections(): void {
		const availableIds = new Set(
			this.matrices.map((organization) => organization.id),
		);

		this.selectedOrganizationIds.forEach((id) => {
			if (!availableIds.has(id)) {
				this.selectedOrganizationIds.delete(id);
			}
		});
	}
}
